// src/containers/pages/caseManagement/utils/autoMapActivities.ts

import { CANtrackComment } from 'containers/pages/comments/types/comment';
import {
  buildActivityMappings,
  ActivityMapping,
} from 'containers/pages/cases/utils/matchActivities';
import type { DestActivity } from '../types';

interface AutoMapResult {
  /** commentId -> destActivityId (null = unmapped, needs manual selection) */
  commentActivityMap: Record<number, number | null>;
  /** The raw activity-level mappings (for debugging/display) */
  mappings: ActivityMapping[];
}

/**
 * Auto-map source comments to destination activities using the tiered
 * matching strategy from buildActivityMappings (exact > contains > AI > manual).
 *
 * For each comment, looks up its source activity in the mapping results
 * and assigns the corresponding destination activity ID.
 *
 * @param comments - Source comments (with their current GPDA)
 * @param destActivities - Activities available on the destination deliverable
 * @returns commentActivityMap (per-comment) + raw mappings (per-activity)
 */
export async function autoMapActivities(
  comments: CANtrackComment[],
  destActivities: DestActivity[],
): Promise<AutoMapResult> {
  // 1. Collect unique source activities and count comments per activity
  const activityMap = new Map<number, { id: number; title: string }>();
  const countMap = new Map<number, number>();

  for (const c of comments) {
    const actId = c.gpda.activity.id;
    // Guard against null/NaN activity IDs
    const safeId = Number.isFinite(actId) ? actId : 0;
    const safeTitle = c.gpda.activity.title || 'Unmapped';

    if (!activityMap.has(safeId)) {
      activityMap.set(safeId, { id: safeId, title: safeTitle });
    }
    countMap.set(safeId, (countMap.get(safeId) ?? 0) + 1);
  }

  const oldActivities = Array.from(activityMap.values());

  // 2. Build mappings via tiered strategy (exact/contains/AI/manual)
  const mappings = await buildActivityMappings(
    oldActivities,
    countMap,
    destActivities,
  );

  // 3. Build per-comment mapping from per-activity mapping
  //    Lookup: sourceActivityId -> destActivityId
  const activityLookup = new Map<number, number | null>();
  for (const m of mappings) {
    activityLookup.set(m.oldActivity.id, m.newActivityId);
  }

  const commentActivityMap: Record<number, number | null> = {};
  for (const c of comments) {
    const actId = c.gpda.activity.id;
    const safeId = Number.isFinite(actId) ? actId : 0;
    commentActivityMap[c.id] = activityLookup.get(safeId) ?? null;
  }

  return { commentActivityMap, mappings };
}
